import React from 'react';
import { Loader2, X, Check, Plug, ShieldCheck } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { useConfirm } from '../ui/ConfirmProvider';

type AppKey = 'stripe' | 'revolut' | 'mollie';

// Apps qui se connectent uniquement par OAuth (aucune clé à saisir) : Stripe Connect et Mollie Connect.
const OAUTH_APPS: AppKey[] = ['stripe', 'mollie'];

interface AppDef {
    key: AppKey;
    name: string;
    description: string;
    logo: React.ReactNode;
    // La clé secrète elle-même n'est plus jamais renvoyée par le serveur (durcissement 2026-09-06) :
    // l'écran ne connaît que « configurée oui/non » et les 4 derniers caractères, pour l'affichage.
    configuredKey: 'stripe_configured' | 'revolut_configured' | 'mollie_configured';
    last4Key: 'stripe_secret_key_last4' | 'revolut_secret_key_last4' | 'mollie_secret_key_last4';
    // Champs de la saisie manuelle d'une clé : uniquement pour les apps SANS connexion directe (Revolut).
    // Stripe n'en a plus : la seule voie est Stripe Connect (décision d'Emeni, 2026-10-14).
    fieldLabel?: string;
    fieldPlaceholder?: string;
    helpText?: string;
    hasSandboxToggle?: boolean;
}

const StripeLogo = () => (
    <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: '#635BFF' }}>
        <span className="text-white font-black text-sm tracking-tight">S</span>
    </div>
);

const RevolutLogo = () => (
    <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: '#000000' }}>
        <span className="text-white font-black text-sm tracking-tight">R</span>
    </div>
);

const MollieLogo = () => (
    <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: '#0A0A0A' }}>
        <span className="text-white font-black text-sm tracking-tight">M</span>
    </div>
);

const APPS: AppDef[] = [
    {
        key: 'stripe',
        name: 'Stripe',
        description: "Ajoute automatiquement un lien de paiement en ligne dans vos emails de facture et vos demandes de paiement (acompte ou intégral) via Démarches client.",
        logo: <StripeLogo />,
        configuredKey: 'stripe_configured',
        last4Key: 'stripe_secret_key_last4',
    },
    {
        key: 'mollie',
        name: 'Mollie',
        description: "Alternative à Stripe pour les mêmes liens de paiement (factures, acomptes/paiements Démarches client) — vous connectez votre propre compte Mollie, sans clé à copier.",
        logo: <MollieLogo />,
        configuredKey: 'mollie_configured',
        last4Key: 'mollie_secret_key_last4',
    },
    {
        key: 'revolut',
        name: 'Revolut Business',
        description: "Alternative à Stripe pour les mêmes liens de paiement (factures, acomptes/paiements Démarches client) — nécessite un compte Revolut Business avec Compte Marchand activé.",
        logo: <RevolutLogo />,
        configuredKey: 'revolut_configured',
        last4Key: 'revolut_secret_key_last4',
        fieldLabel: 'Clé secrète API Revolut',
        fieldPlaceholder: 'sk_...',
        helpText: "Trouvable dans votre tableau de bord Revolut Business, sous Merchant > Paramètres API.",
        hasSandboxToggle: true,
    },
];

/** Onglet "Application" des Paramètres — hub de connexion aux outils tiers (Stripe et Revolut pour
 *  l'instant : chaque entrée d'APPS suffit à en brancher une nouvelle).
 *
 *  Depuis le durcissement du 2026-09-06, la clé secrète de chaque app suit un chemin séparé du reste
 *  des paramètres : elle part par PUT /facture/payment-keys (chiffrée au repos, tracée dans le journal
 *  d'audit) et ne redescend JAMAIS vers le navigateur. Cet écran ne connaît donc, pour chaque app, que
 *  deux choses : « une clé est-elle enregistrée » et ses 4 derniers caractères. Remplacer une clé =
 *  en saisir une nouvelle ; il n'est plus possible de relire l'ancienne, par construction. */
export const ApplicationsSettings: React.FC = () => {
    const [settings, setSettings] = React.useState<Record<string, any>>({});
    const [loading, setLoading] = React.useState(true);
    const confirmAction = useConfirm();
    const [openApp, setOpenApp] = React.useState<AppDef | null>(null);
    const [fieldValue, setFieldValue] = React.useState('');
    const [sandboxValue, setSandboxValue] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');
    const [connectBusy, setConnectBusy] = React.useState(false);
    const [connectMessage, setConnectMessage] = React.useState<{ type: 'ok' | 'error'; text: string } | null>(null);

    const load = () => {
        moduleApi.getBillingSettings().then((s: any) => setSettings(s || {})).catch((e: any) => console.error('Failed to load applications settings', e)).finally(() => setLoading(false));
    };

    React.useEffect(() => { load(); }, []);

    // Retour du aller-retour OAuth Stripe Connect (2026-09-26) : le backend redirige ici avec
    // ?stripe_connect=ok|error — jamais un état à retenir plus d'une fois, l'URL est nettoyée aussitôt lue.
    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const prestataire = params.get('stripe_connect') ? 'Stripe' : params.get('mollie_connect') ? 'Mollie' : '';
        const statut = params.get('stripe_connect') || params.get('mollie_connect');
        if (!statut) return;
        if (statut === 'ok') {
            setConnectMessage({ type: 'ok', text: `Compte ${prestataire} connecté avec succès.` });
        } else {
            const raisons: Record<string, string> = {
                refuse: `Vous avez annulé la connexion à ${prestataire} — rien n'a été modifié.`,
                lien_expire: 'Le lien a expiré (plus de 10 minutes). Réessayez.',
                lien_invalide: 'Lien de retour invalide. Réessayez depuis Paramètres.',
                echange_refuse: `${prestataire} a refusé la connexion. Réessayez, ou contactez le support ${prestataire} si ça persiste.`,
                plateforme_non_configuree: `${prestataire} Connect n'est pas encore activé sur cette plateforme.`,
                chiffrement_absent: 'Erreur technique (chiffrement). Contactez le support.',
                profil_introuvable: "Aucun profil de paiement trouvé sur ce compte Mollie. Terminez la configuration de votre compte Mollie, puis réessayez.",
            };
            setConnectMessage({ type: 'error', text: raisons[params.get('reason') || ''] || `La connexion à ${prestataire} a échoué. Réessayez.` });
        }
        params.delete('stripe_connect'); params.delete('mollie_connect'); params.delete('reason'); params.delete('tab');
        const reste = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (reste ? `?${reste}` : ''));
    }, []);

    const startOAuthConnect = async (app: AppKey) => {
        const nom = app === 'mollie' ? 'Mollie' : 'Stripe';
        setConnectBusy(true); setError('');
        try {
            const res: any = app === 'mollie' ? await moduleApi.getMollieConnectStartUrl() : await moduleApi.getStripeConnectStartUrl();
            if (res?.error || !res?.url) { setError(res?.error || `La connexion à ${nom} n'a pas pu démarrer.`); setConnectBusy(false); return; }
            window.location.href = res.url;
        } catch (e: any) {
            setError(e?.message || `La connexion à ${nom} n'a pas pu démarrer.`);
            setConnectBusy(false);
        }
    };

    const openModal = (app: AppDef) => {
        setError('');
        // Champ toujours vide à l'ouverture : la clé enregistrée ne redescend jamais du serveur. On
        // affiche seulement « •••• 4 derniers caractères » sous le champ pour se repérer.
        setFieldValue('');
        setSandboxValue(!!settings.revolut_sandbox);
        setOpenApp(app);
    };

    const handleSave = async () => {
        // Stripe et Mollie n'ont pas de saisie manuelle : seul Revolut passe par ce formulaire.
        if (!openApp || OAUTH_APPS.includes(openApp.key)) return;
        setSaving(true);
        setError('');
        try {
            const apiKey = fieldValue.trim();
            if (!apiKey) {
                setError('Saisissez la clé secrète, ou utilisez « Déconnecter » pour la supprimer.');
                setSaving(false);
                return;
            }
            // Route dédiée aux secrets : chiffrement au repos, journal d'audit, titulaire uniquement.
            const res: any = await moduleApi.savePaymentKey({
                provider: openApp.key, api_key: apiKey,
                ...(openApp.hasSandboxToggle ? { sandbox: sandboxValue } : {}),
            });
            // Le prestataire tout juste connecté devient le prestataire actif — cohérent avec l'intention
            // de l'établissement à cet instant précis (il vient de le configurer, il a probablement
            // l'intention de s'en servir). Reste modifiable ensuite via le sélecteur si les deux sont connectés.
            const payload: Record<string, any> = { ...settings, active_payment_provider: openApp.key };
            if (openApp.hasSandboxToggle) payload.revolut_sandbox = sandboxValue;
            await moduleApi.saveBillingSettings(payload);
            setSettings({ ...payload, [openApp.configuredKey]: true, [openApp.last4Key]: res?.last4 || apiKey.slice(-4) });
            setOpenApp(null);
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la sauvegarde.');
        } finally {
            setSaving(false);
        }
    };

    const setActiveProvider = async (provider: AppKey) => {
        setSaving(true);
        try {
            const payload = { ...settings, active_payment_provider: provider };
            await moduleApi.saveBillingSettings(payload);
            setSettings(payload);
        } catch (e: any) {
            console.error('Failed to set active payment provider', e);
        } finally {
            setSaving(false);
        }
    };

    const handleDisconnect = async (app: AppDef) => {
        if (!(await confirmAction({ title: 'Déconnecter l\'application ?', description: <>La clé de {app.name} sera effacée ; les paiements par cette application cesseront de fonctionner.</>, confirmLabel: 'Déconnecter' }))) return;
        setSaving(true);
        try {
            await moduleApi.savePaymentKey({ provider: app.key, api_key: '' });
            setSettings({ ...settings, [app.configuredKey]: false, [app.last4Key]: '' });
        } catch (e: any) {
            console.error('Failed to disconnect app', e);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;
    }

    const connectedProviders = APPS.filter((app) => !!settings[app.configuredKey]);

    return (
        <div className="max-w-2xl space-y-4">
            <p className="text-xs text-content-muted italic">Connectez des outils tiers à votre établissement. D'autres applications viendront s'ajouter ici prochainement.</p>

            {connectMessage && (
                <div className={`p-3 rounded-xl border text-sm font-semibold flex items-center justify-between gap-3 ${connectMessage.type === 'ok' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500' : 'bg-red-500/10 border-red-500/20 text-red-400'}`}>
                    <span>{connectMessage.text}</span>
                    <button onClick={() => setConnectMessage(null)} className="shrink-0 opacity-70 hover:opacity-100"><X size={14} /></button>
                </div>
            )}

            {connectedProviders.length > 1 && (
                <div className="p-4 rounded-2xl bg-surface-soft border border-border space-y-2">
                    <p className="text-xs font-bold text-content">Moyen de paiement actif pour les liens envoyés (factures, Démarches client)</p>
                    <div className="flex gap-2">
                        {connectedProviders.map((app) => (
                            <button key={app.key} onClick={() => setActiveProvider(app.key)} disabled={saving}
                                className={`px-3 py-2 rounded-xl text-xs font-bold border transition-all ${settings.active_payment_provider === app.key ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                {app.name}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {APPS.map((app) => {
                const isConnected = !!settings[app.configuredKey];
                return (
                    <div key={app.key} className="flex items-center gap-4 p-4 rounded-2xl bg-surface-soft border border-border">
                        {app.logo}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <p className="font-bold text-content text-sm">{app.name}</p>
                                {isConnected && app.key === 'stripe' && settings.stripe_connect_account_id ? (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase px-2 py-0.5 rounded-lg bg-emerald-500/10 text-emerald-500 border border-emerald-500/20" title={`Compte ${settings.stripe_connect_account_id}`}>
                                        <ShieldCheck size={10} /> Connecté via Stripe Connect
                                    </span>
                                ) : isConnected && app.key === 'mollie' ? (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase px-2 py-0.5 rounded-lg bg-emerald-500/10 text-emerald-500 border border-emerald-500/20" title={settings.mollie_organization_id ? `Organisation ${settings.mollie_organization_id}` : undefined}>
                                        <ShieldCheck size={10} /> Connecté via Mollie Connect
                                    </span>
                                ) : isConnected && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase px-2 py-0.5 rounded-lg bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                                        <Check size={10} /> Connecté
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-content-muted mt-0.5">{app.description}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            {isConnected && (
                                <button onClick={() => handleDisconnect(app)} disabled={saving} className="px-3 py-2 rounded-xl border border-border text-content-muted hover:text-red-400 hover:border-red-500/30 text-xs font-bold transition-all disabled:opacity-50">
                                    Déconnecter
                                </button>
                            )}
                            <button onClick={() => openModal(app)} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-xs font-bold transition-all">
                                <Plug size={13} /> {isConnected ? 'Modifier' : 'Connecter'}
                            </button>
                        </div>
                    </div>
                );
            })}

            {openApp && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-surface border border-border rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
                        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-content flex items-center gap-2">
                                {openApp.logo} Connecter {openApp.name}
                            </h3>
                            <button onClick={() => setOpenApp(null)} className="text-content-muted hover:text-gray-700 dark:hover:text-slate-900 transition-colors">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            {OAUTH_APPS.includes(openApp.key) && (
                                <div className="space-y-3">
                                    <div className="flex items-start gap-2.5 p-3 rounded-xl bg-surface-soft border border-border">
                                        <ShieldCheck size={18} className="text-brand-600 dark:text-brand-400 shrink-0 mt-0.5" />
                                        <p className="text-xs text-content-muted leading-relaxed">
                                            Vous gardez votre propre compte {openApp.name} (votre IBAN, vos frais) — L'IAmani ne touche jamais à votre argent.
                                            Aucune clé à copier-coller : vous autorisez la connexion directement sur {openApp.key === 'mollie' ? 'mollie.com' : 'stripe.com'}.
                                        </p>
                                    </div>
                                    <button type="button" onClick={() => startOAuthConnect(openApp.key)} disabled={connectBusy}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-black disabled:opacity-50">
                                        {connectBusy ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />} Connecter mon compte {openApp.name}
                                    </button>
                                </div>
                            )}
                            {!OAUTH_APPS.includes(openApp.key) && (
                            <>
                            <div className="space-y-1.5">
                                <label className="text-xs font-bold text-content-muted uppercase tracking-widest">{openApp.fieldLabel}</label>
                                <input
                                    type="password"
                                    autoFocus
                                    value={fieldValue}
                                    onChange={(e) => setFieldValue(e.target.value)}
                                    placeholder={openApp.fieldPlaceholder}
                                    className="w-full bg-surface-soft border border-border rounded-xl px-4 py-3 text-sm text-content outline-none focus:border-brand-500 transition-colors font-mono"
                                />
                                {!!settings[openApp.configuredKey] && (
                                    <p className="text-xs text-content-muted">
                                        Une clé est déjà enregistrée{settings[openApp.last4Key] ? ` (se termine par ${settings[openApp.last4Key]})` : ''}. Pour des raisons de sécurité elle ne peut plus être relue : saisissez-en une nouvelle pour la remplacer.
                                    </p>
                                )}
                                <p className="text-xs text-content-muted">{openApp.helpText}</p>
                            </div>
                            {openApp.hasSandboxToggle && (
                                <label className="flex items-center justify-between cursor-pointer p-3 rounded-xl bg-surface-soft border border-border">
                                    <span className="text-xs font-bold text-content">Mode test (sandbox)</span>
                                    <input type="checkbox" checked={sandboxValue} onChange={(e) => setSandboxValue(e.target.checked)} className="accent-brand-600 w-4 h-4" />
                                </label>
                            )}
                            <div className="flex gap-3 pt-2">
                                <button onClick={() => setOpenApp(null)} disabled={saving} className="flex-1 px-4 py-3 rounded-xl border border-border text-slate-700 dark:text-content-muted font-bold hover:bg-gray-50 dark:hover:bg-surface-soft transition-colors disabled:opacity-60">
                                    Annuler
                                </button>
                                <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold disabled:opacity-50 transition-colors">
                                    {saving ? 'Enregistrement...' : 'Enregistrer'}
                                </button>
                            </div>
                            </>
                            )}
                            {error && <p className="text-xs text-red-400 font-semibold">{error}</p>}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
